import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IContacts } from "../../../domain/models/Contacts";
import { contactsServiceFactory } from "../../../services/ContactsServiceImpl";
import ContactsEditor from "./ContactsEditor";
import LoadingPanel from "../../../custom-components/CustomLoader";

const ContactsPage: FC = () => {
    const { t } = useTranslation();
    const contactsService = contactsServiceFactory();
    const [contacts, setContacts] = useState<IContacts | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditorOpen, setIsEditorOpen] = useState(false);

    useEffect(() => {
        const loadContacts = async () => {
            try {
                setIsLoading(true);
                const data = await contactsService.getContacts();
                setContacts(data);
            } catch (err) {
                console.log("Error loading contacts", err);
            } finally {
                setIsLoading(false);
            }
        };
        loadContacts();
    }, []);

    const handleSave = async (saved: IContacts) => {
        setContacts(saved);
        setIsEditorOpen(false);
        try {
            const data = await contactsService.getContacts();
            setContacts(data);
        } catch (err) {
            console.log("Error reloading contacts", err);
        }
    };

    if (isLoading) {
        return (
            <div className="relative z-0">
                <LoadingPanel isVisible={isLoading} />
            </div>
        );
    }

    return (
        <div className="relative z-0">
            <div className="bg-light-bg-100 dark:bg-dark-bg-100 w-[95%] mb-[10px] mt-[10px] mx-auto py-2 px-3 flex items-center justify-between shadow-md rounded-xl" style={{ minHeight: '64px' }}>
                <h2 className="text-[20px] font-bold text-light-text-100 dark:text-dark-text-100 ml-4">
                    {t("console.sidebar.contactsPage")}
                </h2>
                <div className="flex items-center gap-4">
                <button
                    className="bg-green-500 hover:bg-green-600 fill-white flex items-center text-white focus:ring-2 focus:outline-none font-medium rounded-lg text-sm px-4 py-2.5 text-center h-10"
                    onClick={() => setIsEditorOpen(true)}
                >
                        {contacts ? t("console.news.edit") : t("console.common.create")}
                </button>
                </div>
            </div>

            <div className="w-[95%] mx-auto py-6">
                {contacts ? (
                    <div className="bg-light-bg-200 dark:bg-dark-bg-700 rounded-lg p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-light-text-200 dark:text-dark-text-100 mb-1">
                                    {t("console.contacts.table.address")}
                                </label>
                                <p className="text-lg text-light-text-100 dark:text-dark-text-100">
                                    {contacts.address}
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-light-text-200 dark:text-dark-text-100 mb-1">
                                    {t("console.contacts.table.phone")}
                                </label>
                                <p className="text-lg text-light-text-100 dark:text-dark-text-100">{contacts.phone}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-light-text-200 dark:text-dark-text-100 mb-1">
                                    {t("console.contacts.table.email")}
                                </label>
                                <p className="text-lg text-light-text-100 dark:text-dark-text-100">{contacts.email}</p>
                            </div>
                            {contacts.directorId && (
                                <div>
                                    <label className="block text-sm font-medium text-light-text-200 dark:text-dark-text-200 mb-1">
                                        {t("console.contacts.table.director")}
                                    </label>
                                    <p className="text-lg text-light-text-100 dark:text-dark-text-100">
                                        {contacts.directorId}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <p className="text-lg text-light-text-200 dark:text-dark-text-100">
                            {t("console.contacts.noContacts")}
                        </p>
                    </div>
                )}
            </div>

            {isEditorOpen && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
                    <div className="w-full max-w-4xl">
                        <ContactsEditor entity={contacts} onClose={() => setIsEditorOpen(false)} onSave={handleSave} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactsPage;
